// src/components/ActingBanner.tsx
//
// The bar that says whose devices and whose content you are about to change.
//
// It is not decoration and it is not a nicety. A super admin acting as a
// customer holds a token whose CompanyId is that customer's, which is what makes
// every existing page work unchanged - and equally means nothing else on screen
// will tell them where they are. This is the thing that tells them.
import React, { useEffect, useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * "52 min left", "4 min left", "under a minute".
 *
 * Shown because the acting token lasts an hour and then stops working. Watching
 * it run down is a great deal better than finding out when a save fails.
 */
const describeRemaining = (expiresAt: Date): string => {
  const msLeft = expiresAt.getTime() - Date.now();
  if (msLeft <= 0) return 'expired';

  const minutes = Math.floor(msLeft / 60000);
  if (minutes < 1) return 'under a minute left';
  return `${minutes} min left`;
};

const ActingBanner: React.FC = () => {
  const { user, actingAs, actingExpiresAt, stopActing } = useAuth();
  const [returning, setReturning] = useState(false);
  const [, setTick] = useState(0);

  // Re-render once a minute so the countdown is not stale. Cheap, and only ever
  // mounted while acting.
  useEffect(() => {
    if (!actingExpiresAt) return;
    const timer = window.setInterval(() => setTick(t => t + 1), 30000);
    return () => window.clearInterval(timer);
  }, [actingExpiresAt]);

  if (!actingAs) return null;

  const handleReturn = async () => {
    setReturning(true);
    try {
      await stopActing();
    } finally {
      setReturning(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-300 bg-amber-100 px-4 py-2 sm:px-6 lg:px-8">
      <Eye className="h-4 w-4 flex-shrink-0 text-amber-800" aria-hidden="true" />

      <p className="text-sm text-amber-900">
        Working on <span className="font-medium">{actingAs.companyName}</span>
        <span className="hidden sm:inline"> — changes affect their devices and content</span>
      </p>

      <div className="ml-auto flex items-center gap-3">
        {actingExpiresAt && (
          <span className="text-xs text-amber-800">{describeRemaining(actingExpiresAt)}</span>
        )}

        <button
          type="button"
          onClick={handleReturn}
          disabled={returning}
          className="flex items-center gap-1.5 rounded border border-amber-400 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
        >
          {returning && <Loader2 className="h-3 w-3 animate-spin" />}
          Return to {user?.companyName ?? 'your own company'}
        </button>
      </div>
    </div>
  );
};

export default ActingBanner;
